// A simple hash function for the product ID to get a consistent seed value
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

// Seeded random number generator function
function seededRandom(seed: number): () => number {
  return function (): number {
    const x = Math.sin(seed++) * 10000
    return x - Math.floor(x)
  }
}

function _complexLogic(base: number, factor: number, randomFunc: () => number): number {
  return Math.round(base * randomFunc() * factor)
}

function getBaseCountFromHash(hash: number, scale = 10, minThreshold = 1): number {
  return Math.abs(hash % scale) + minThreshold
}

interface ProductMetrics {
  soldCount: number
  soldCountLast24h: number
  repeatPurchaseCount: number
  currentlyViewingCount: number
}

export const useProductMetricsStore = defineStore('productMetrics', () => {
  const metricsCache = useState<Record<string, ProductMetrics>>(() => ({}))

  // Fixed timestamp values for consistent results
  const timestamp = Date.now()
  const hourOfDay = new Date(timestamp).getHours()
  const daySeed = Math.floor(timestamp / (1000 * 60 * 60 * 24))
  const minuteSeed = Math.floor(timestamp / (1000 * 60 * 5))

  // Get metrics for a product
  function getMetricsForProduct(productId: string, reviewCount = 0): ProductMetrics {
    const cacheKey = `${productId}-${reviewCount}-${daySeed}`
    if (metricsCache.value[cacheKey]) {
      return metricsCache.value[cacheKey]
    }

    const productHash = hashString(productId) + daySeed
    const randomFunc = seededRandom(productHash)

    let totalSoldCount: number
    let soldCountLast24h: number
    let repeatPurchaseCount: number

    // Peak hours determination (used for both cases)
    const isPeakHour = (hourOfDay >= 9 && hourOfDay < 12) || (hourOfDay >= 18 && hourOfDay < 21)

    if (reviewCount > 0) {
      // Calculate total sales based on review count (11-13x multiplier)
      const reviewMultiplier = 11 + (randomFunc() * 2) // Random between 11-13
      totalSoldCount = Math.round(reviewCount * reviewMultiplier)

      // Calculate last 24h sales as a percentage of total (varies by time of day)
      // Peak hours get higher percentage of daily sales
      const dailySalesPercentage = isPeakHour ? 0.002 : 0.001 // 0.1-0.2% of total sales per day
      const hourlyVariation = 0.5 + randomFunc() * 1 // 0.5x to 1.5x variation
      soldCountLast24h = Math.max(1, Math.round(totalSoldCount * dailySalesPercentage * hourlyVariation))

      // Calculate repeat purchases (5-10% of total)
      const repeatPercentage = 0.05 + randomFunc() * 0.05
      repeatPurchaseCount = Math.round(totalSoldCount * repeatPercentage)
    }
    else {
      // Use previous behavior when no reviews
      const baseCountForSold = getBaseCountFromHash(productHash, 500, 250)
      const baseCountForRepeat = getBaseCountFromHash(productHash, 20, 10)

      // Factor calculation
      const factorForSold = hourOfDay < 12 ? 0.8 : 1.5
      const factorForRepeat = (hourOfDay >= 12 && hourOfDay < 18) ? 1.2 : 0.9

      const generateCount = (baseCount: number, factor: number) => _complexLogic(baseCount, factor, randomFunc)

      soldCountLast24h = generateCount(baseCountForSold, factorForSold)
      // Total sold count should be much higher than last 24h (simulate historical sales)
      // Use a multiplier to make it look like accumulated sales over time
      const historicalMultiplier = 10 + randomFunc() * 20 // 10-30x multiplier for total sales
      totalSoldCount = Math.round(soldCountLast24h * historicalMultiplier)
      repeatPurchaseCount = generateCount(baseCountForRepeat, factorForRepeat)
    }

    // Calculate currently viewing
    const baseCountForViewing = getBaseCountFromHash(productHash, 50, 4)
    const factorForViewing = isPeakHour ? 1.7 : 0.7
    const viewingVariation = seededRandom(productHash + minuteSeed)() * 0.3 + 0.85
    const currentlyViewingCount = Math.max(1, Math.round(baseCountForViewing * factorForViewing * viewingVariation))

    // Create and cache the metrics
    const metrics = {
      soldCount: totalSoldCount,
      soldCountLast24h,
      repeatPurchaseCount,
      currentlyViewingCount,
    }
    metricsCache.value[cacheKey] = metrics

    return metrics
  }

  return {
    metricsCache,
    getMetricsForProduct,
  }
})
